import type { CareActionItem, CareActionKind } from '@/types/interfaces/careActions'

export type CareActionSectionId = 'next_steps' | 'updates' | 'coming_up' | 'medications' | 'history'

export type CareActionKindSectionId = 'prescriptions' | 'labs' | 'follow_up_visit' | 'other_care'

/** How a section renders each row's secondary line. */
export type CareActionSecondaryLine = 'status' | 'date'

export interface CareActionSectionDefinition {
    id: CareActionSectionId | CareActionKindSectionId
    heading: string
    secondaryLine: CareActionSecondaryLine
    /** Shows a count beside the heading. */
    showCount?: boolean
}

/**
 * Care-plan detail sections in display order. A new care object kind joins an
 * existing section through the state/kind maps below; a genuinely new section
 * needs only an entry here.
 */
export const CARE_ACTION_SECTIONS: readonly CareActionSectionDefinition[] = [
    { id: 'next_steps', heading: 'Next steps', secondaryLine: 'status', showCount: true },
    { id: 'updates', heading: 'Updates', secondaryLine: 'status' },
    { id: 'coming_up', heading: 'Coming up', secondaryLine: 'status' },
    { id: 'medications', heading: 'Medications', secondaryLine: 'status' },
    { id: 'history', heading: 'History', secondaryLine: 'date' },
]

/**
 * Canonical state -> section for every care object, mirroring how
 * `careActionAppearance` keys tone off state alone: an object in the same state
 * lands in the same section no matter which kind it is. States are the raw model
 * states (see Lab::Order, Appointment, Care::Plan).
 */
const SECTION_BY_STATE: Record<string, CareActionSectionId> = {
    // Care actions (CareAction::Imaging / Referral / Lab) the patient must act on.
    awaiting_customer_selection: 'next_steps',
    awaiting_customer_selection_with_search: 'next_steps',
    awaiting_billing: 'next_steps',
    // Selection made; the booking is being submitted downstream.
    awaiting_submission: 'updates',
    processing: 'updates',

    // Labs (Lab::Order) — unbooked, or booked for a visit.
    init: 'next_steps',
    pending: 'coming_up',
    requisition_created: 'coming_up',
    // Labs in flight with the lab: results accrue as updates.
    ordered: 'updates',
    received_order: 'updates',
    partial_results: 'updates',

    // Visits (Appointment) still ahead of the patient, including a live hold
    // the patient has not finished converting into a booking.
    booked: 'coming_up',
    scheduled: 'coming_up',
    confirmed: 'coming_up',
    awaiting_confirmation: 'coming_up',
    ready_for_visit: 'coming_up',
    held: 'coming_up',
    in_progress: 'updates',

    // Care plans (Care::Plan, incl. communication_state) being prepared.
    awaiting_curation: 'updates',
    awaiting_external_work: 'updates',
    awaiting_generation: 'updates',
    awaiting_first_send: 'updates',
    awaiting_curation_send: 'updates',
    in_review: 'updates',

    // Finished work, shared across every kind.
    completed: 'history',
    complete: 'history',
    sent: 'history',
    send_complete: 'history',
    first_send_complete: 'history',
    resulted: 'history',

    // Terminal without completing. Mapped explicitly so they read as settled
    // rather than falling through to the in-flight default, even though the
    // condition-groups payload filters them out today.
    canceled: 'history',
    cancelled: 'history',
    hold_expired: 'history',
    abandoned: 'history',
    replaced: 'history',
    patient_no_show: 'history',
    encounter_deleted: 'history',
}

/**
 * Kinds whose section never depends on state. Medications are listed rather
 * than sectioned by lifecycle, and `patient_medication` rows carry no state at
 * all, so they resolve here regardless of state.
 */
const SECTION_BY_KIND: Partial<Record<CareActionKind, CareActionSectionId>> = {
    patient_medication: 'medications',
}

/**
 * Section a care action belongs to. Unrecognized states fall to Updates rather
 * than History, so a genuinely new upstream state reads as still in flight
 * instead of being buried as finished work. Every state the models can reach
 * today — including the terminal ones — is mapped, so the default only catches
 * states that did not exist when this was written.
 */
export function careActionSection(action: CareActionItem): CareActionSectionId {
    return (
        SECTION_BY_KIND[action.kind] ??
        SECTION_BY_STATE[(action.state ?? '').toLowerCase()] ??
        'updates'
    )
}

export interface CareActionSectionGroup {
    definition: CareActionSectionDefinition
    actions: CareActionItem[]
}

/**
 * Care actions bucketed into display-ordered sections. Sections keep the
 * newest-first order the API returns, and empty sections are dropped.
 */
export function careActionSections(actions: CareActionItem[]): CareActionSectionGroup[] {
    const actionsBySectionId = new Map<string, CareActionItem[]>()

    actions.forEach((action) => {
        const sectionId = careActionSection(action)
        const existing = actionsBySectionId.get(sectionId)
        if (existing) {
            existing.push(action)
        } else {
            actionsBySectionId.set(sectionId, [action])
        }
    })

    return CARE_ACTION_SECTIONS.filter((definition) => actionsBySectionId.has(definition.id)).map(
        (definition) => ({
            definition,
            actions: actionsBySectionId.get(definition.id) ?? [],
        }),
    )
}

const CARE_ACTION_KIND_SECTIONS: readonly CareActionSectionDefinition[] = [
    { id: 'prescriptions', heading: 'Prescriptions', secondaryLine: 'status' },
    { id: 'labs', heading: 'Labs', secondaryLine: 'status' },
    { id: 'follow_up_visit', heading: 'Follow-up visit', secondaryLine: 'status' },
    { id: 'other_care', heading: 'Other care', secondaryLine: 'status' },
]

const KIND_SECTION_BY_KIND: Partial<Record<CareActionKind, CareActionKindSectionId>> = {
    patient_medication: 'prescriptions',
    medication_check_in: 'prescriptions',
    lab_order: 'labs',
    lab_care_action: 'labs',
    external_lab_result: 'labs',
    appointment: 'follow_up_visit',
    video_visit: 'follow_up_visit',
    chat_visit: 'follow_up_visit',
    video_visit_with_specialist_consultation: 'follow_up_visit',
    in_person_visit_order: 'follow_up_visit',
}

export function careActionKindSections(actions: CareActionItem[]): CareActionSectionGroup[] {
    return CARE_ACTION_KIND_SECTIONS.map((definition) => ({
        definition,
        actions: actions.filter(
            (action) => (KIND_SECTION_BY_KIND[action.kind] ?? 'other_care') === definition.id,
        ),
    })).filter(({ actions: sectionActions }) => sectionActions.length > 0)
}
